/**
 * Single source of truth for domain enums. `packages/database/prisma/schema.prisma`
 * mirrors these exactly — keep both in sync when changing values.
 */
export const UserStatus = { ACTIVE: "ACTIVE", SUSPENDED: "SUSPENDED" };
export const UserRole = { USER: "USER", MODERATOR: "MODERATOR", ADMIN: "ADMIN" };
export const WalletType = { EOA: "EOA", SMART_ACCOUNT: "SMART_ACCOUNT" };
export const ListingStatus = {
    DRAFT: "DRAFT",
    SCANNING: "SCANNING",
    READY: "READY",
    PUBLISHED: "PUBLISHED",
    REJECTED: "REJECTED",
    ARCHIVED: "ARCHIVED",
};
/** Fixed taxonomy for browse filters/category pages — see packages/database's Listing.category
 *  docblock for why this isn't seller-invented free text. */
export const ListingCategory = {
    GAME_ACCOUNTS: "GAME_ACCOUNTS",
    SOCIAL_MEDIA_ACCOUNTS: "SOCIAL_MEDIA_ACCOUNTS",
    GAME_CURRENCY_ITEMS: "GAME_CURRENCY_ITEMS",
    SOFTWARE_LICENSES: "SOFTWARE_LICENSES",
    GIFT_CARDS: "GIFT_CARDS",
    CLOUD_CREDITS: "CLOUD_CREDITS",
    DOMAINS: "DOMAINS",
    SUBSCRIPTIONS: "SUBSCRIPTIONS",
    DIGITAL_ART_COLLECTIBLES: "DIGITAL_ART_COLLECTIBLES",
    EBOOKS_AND_MEDIA: "EBOOKS_AND_MEDIA",
    DESIGN_ASSETS: "DESIGN_ASSETS",
    OTHER: "OTHER",
};
/** Human-readable labels for every category, in the order they should appear in a picker. */
export const LISTING_CATEGORY_LABELS = {
    [ListingCategory.GAME_ACCOUNTS]: "Game accounts",
    [ListingCategory.SOCIAL_MEDIA_ACCOUNTS]: "Social media accounts",
    [ListingCategory.GAME_CURRENCY_ITEMS]: "Game currency & items",
    [ListingCategory.SOFTWARE_LICENSES]: "Software licenses",
    [ListingCategory.GIFT_CARDS]: "Gift cards",
    [ListingCategory.CLOUD_CREDITS]: "Cloud & API credits",
    [ListingCategory.DOMAINS]: "Domain names",
    [ListingCategory.SUBSCRIPTIONS]: "Subscriptions",
    [ListingCategory.DIGITAL_ART_COLLECTIBLES]: "Digital art & collectibles",
    [ListingCategory.EBOOKS_AND_MEDIA]: "Ebooks & media",
    [ListingCategory.DESIGN_ASSETS]: "Design assets & templates",
    [ListingCategory.OTHER]: "Other digital goods",
};
export const LISTING_CATEGORY_VALUES = Object.keys(LISTING_CATEGORY_LABELS);
/** Earned, server-computed trust signals shown next to a seller's listings — never self-declared
 *  and never stored; always recomputed from live order/review data (see
 *  apps/api/src/services/badges.ts) so a badge can never go stale or be gamed by editing a row. */
export const SellerBadge = {
    TOP_RATED: "TOP_RATED",
    TRUSTED_SELLER: "TRUSTED_SELLER",
};
export const SELLER_BADGE_LABELS = {
    [SellerBadge.TOP_RATED]: "Top Rated",
    [SellerBadge.TRUSTED_SELLER]: "Trusted Seller",
};
/** Best Offer negotiation — see packages/database's Offer model docblock. PENDING and COUNTERED
 *  both mean "awaiting a response"; they only differ for display (has there been a counter yet). */
export const OfferStatus = {
    PENDING: "PENDING",
    COUNTERED: "COUNTERED",
    ACCEPTED: "ACCEPTED",
    DECLINED: "DECLINED",
    EXPIRED: "EXPIRED",
};
export const OfferProposerRole = { BUYER: "BUYER", SELLER: "SELLER" };
/** How a listing's product reaches the buyer — see packages/database's ListingFulfillmentType
 *  docblock. Immutable after listing creation. */
export const ListingFulfillmentType = {
    FILE: "FILE",
    LICENSE_KEY: "LICENSE_KEY",
    ACCOUNT_TRANSFER: "ACCOUNT_TRANSFER",
    DOMAIN_TRANSFER: "DOMAIN_TRANSFER",
    NFT_TRANSFER: "NFT_TRANSFER",
};
export const LicenseKeyStatus = { AVAILABLE: "AVAILABLE", CLAIMED: "CLAIMED" };
/** Which third-party platform an ACCOUNT_TRANSFER listing's account lives on — see
 *  packages/database's AccountPlatform docblock. */
export const AccountPlatform = {
    YOUTUBE: "YOUTUBE",
    X_TWITTER: "X_TWITTER",
    TIKTOK: "TIKTOK",
    INSTAGRAM: "INSTAGRAM",
    DISCORD: "DISCORD",
    OTHER: "OTHER",
};
export const ACCOUNT_PLATFORM_LABELS = {
    [AccountPlatform.YOUTUBE]: "YouTube",
    [AccountPlatform.X_TWITTER]: "X (Twitter)",
    [AccountPlatform.TIKTOK]: "TikTok",
    [AccountPlatform.INSTAGRAM]: "Instagram",
    [AccountPlatform.DISCORD]: "Discord",
    [AccountPlatform.OTHER]: "Other",
};
export const ACCOUNT_PLATFORM_VALUES = Object.keys(ACCOUNT_PLATFORM_LABELS);
/** Where an ACCOUNT_TRANSFER order sits in the manual custodial handoff — see
 *  packages/database's AccountTransferStage docblock for the full design rationale. */
export const AccountTransferStage = {
    AWAITING_SELLER_HANDOFF: "AWAITING_SELLER_HANDOFF",
    PLATFORM_VERIFYING: "PLATFORM_VERIFYING",
    READY_FOR_BUYER: "READY_FOR_BUYER",
    CREDENTIALS_REVEALED: "CREDENTIALS_REVEALED",
};
export const ACCOUNT_TRANSFER_STAGE_LABELS = {
    [AccountTransferStage.AWAITING_SELLER_HANDOFF]: "Awaiting seller handoff",
    [AccountTransferStage.PLATFORM_VERIFYING]: "Platform verifying",
    [AccountTransferStage.READY_FOR_BUYER]: "Ready for buyer",
    [AccountTransferStage.CREDENTIALS_REVEALED]: "Credentials revealed",
};
/** Where a DOMAIN_TRANSFER order sits — see services/domainTransfers.ts. One fewer stage than
 *  AccountTransferStage: there's no PLATFORM_VERIFYING equivalent, since nothing about a transfer
 *  code can be meaningfully checked without actually attempting a real transfer. */
export const DomainTransferStage = {
    AWAITING_SELLER_CODE: "AWAITING_SELLER_CODE",
    READY_FOR_BUYER: "READY_FOR_BUYER",
    CODE_REVEALED: "CODE_REVEALED",
};
export const DOMAIN_TRANSFER_STAGE_LABELS = {
    [DomainTransferStage.AWAITING_SELLER_CODE]: "Awaiting seller's transfer code",
    [DomainTransferStage.READY_FOR_BUYER]: "Ready for buyer",
    [DomainTransferStage.CODE_REVEALED]: "Code revealed",
};
/** Where an NFT_TRANSFER order sits — see services/nftTransfers.ts. Only two stages, fewer even
 *  than DomainTransferStage: there's no seller "submission" step to model at all (nothing is ever
 *  encrypted or stored — the seller sends the NFT directly to the buyer's own wallet using their
 *  own wallet app/marketplace, entirely outside this platform), so the only thing left to track is
 *  whether an on-chain `ownerOf` read has yet confirmed the buyer's wallet holds it. */
export const NftTransferStage = {
    AWAITING_TRANSFER: "AWAITING_TRANSFER",
    DELIVERED: "DELIVERED",
};
export const NFT_TRANSFER_STAGE_LABELS = {
    [NftTransferStage.AWAITING_TRANSFER]: "Awaiting NFT transfer",
    [NftTransferStage.DELIVERED]: "Delivered",
};
/** In-app notification categories — see packages/database's Notification model docblock. */
export const NotificationType = {
    MESSAGE_RECEIVED: "MESSAGE_RECEIVED",
    OFFER_RECEIVED: "OFFER_RECEIVED",
    OFFER_COUNTERED: "OFFER_COUNTERED",
    OFFER_ACCEPTED: "OFFER_ACCEPTED",
    OFFER_DECLINED: "OFFER_DECLINED",
    TICKET_REPLY: "TICKET_REPLY",
    WATCHLIST_PRICE_DROP: "WATCHLIST_PRICE_DROP",
    SAVED_SEARCH_MATCH: "SAVED_SEARCH_MATCH",
    ORDER_FUNDED: "ORDER_FUNDED",
    ORDER_RELEASED: "ORDER_RELEASED",
    ORDER_DISPUTED: "ORDER_DISPUTED",
    DISPUTE_RESOLVED: "DISPUTE_RESOLVED",
    ACCOUNT_TRANSFER_READY: "ACCOUNT_TRANSFER_READY",
    ACCOUNT_TRANSFER_DEADLINE_APPROACHING: "ACCOUNT_TRANSFER_DEADLINE_APPROACHING",
    ACCOUNT_TRANSFER_DEADLINE_URGENT: "ACCOUNT_TRANSFER_DEADLINE_URGENT",
    DOMAIN_TRANSFER_READY: "DOMAIN_TRANSFER_READY",
    NFT_TRANSFER_DELIVERED: "NFT_TRANSFER_DELIVERED",
};
export const UploadStatus = {
    PRESIGNED: "PRESIGNED",
    UPLOADED: "UPLOADED",
    SCANNING: "SCANNING",
    CLEAN: "CLEAN",
    INFECTED: "INFECTED",
    FAILED: "FAILED",
    DELETED: "DELETED",
};
export const FinancialStatus = {
    PENDING_FUNDING: "PENDING_FUNDING",
    FUNDED: "FUNDED",
    DISPUTED: "DISPUTED",
    RELEASED: "RELEASED",
    REFUNDED: "REFUNDED",
    EXPIRED: "EXPIRED",
    FAILED: "FAILED",
};
export const DeliveryStatus = {
    NOT_ENTITLED: "NOT_ENTITLED",
    AVAILABLE: "AVAILABLE",
    DOWNLOADED: "DOWNLOADED",
    REVOKED: "REVOKED",
};
export const DisputeStatus = {
    PENDING_ONCHAIN: "PENDING_ONCHAIN",
    OPEN: "OPEN",
    UNDER_REVIEW: "UNDER_REVIEW",
    RESOLVED: "RESOLVED",
    REJECTED: "REJECTED",
};
/** Support tickets — deliberately separate from `DisputeStatus`/disputes (a legal/financial
 *  escrow disagreement) — this is general account/product support. See packages/database's
 *  Ticket model docblock. */
export const TicketStatus = {
    NEW: "NEW",
    PENDING: "PENDING",
    RESOLVED: "RESOLVED",
    CANCELLED: "CANCELLED",
};
export const TicketPriority = {
    CRITICAL: "CRITICAL",
    HIGH: "HIGH",
    NORMAL: "NORMAL",
    LOW: "LOW",
};
export const TicketContactMethod = {
    EMAIL: "EMAIL",
    PHONE: "PHONE",
};
/** A blog post's lifecycle — see packages/database's Post model docblock. Just two states; a
 *  DRAFT post is absent from every public /blog surface, not shown-but-flagged. */
export const PostStatus = { DRAFT: "DRAFT", PUBLISHED: "PUBLISHED" };
export const ChainEventStatus = {
    OBSERVED: "OBSERVED",
    CONFIRMED: "CONFIRMED",
    ORPHANED: "ORPHANED",
    APPLIED: "APPLIED",
};
/** Financial states in which a buyer's download entitlement remains valid. */
export const DOWNLOAD_ELIGIBLE_STATUSES = [
    FinancialStatus.FUNDED,
    FinancialStatus.DISPUTED,
    FinancialStatus.RELEASED,
];
//# sourceMappingURL=enums.js.map